import { Bug } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMobileFabItem } from "@/components/layout/mobile-fab";
import { FeedbackSubmitDialog } from "./feedback-submit-dialog";
import { closeFeedbackPanel, openFeedbackPanel, useFeedbackPanelOpen } from "./feedback-panel-store";
import { useFeedbackBoardStatus } from "./use-feedback-board";

/**
 * Floating "Bugs & Features" button. Renders nothing until the board status
 * loads, so it stays hidden while the flag is off. Below lg it becomes the
 * "Report a bug / idea" item in the mobile FAB menu instead.
 *
 * It is the single owner of the floating panel: the panel's open state lives in
 * the shared store, so the /feedback page button and the mobile FAB item open
 * this one panel rather than each mounting their own. FeedbackButton is mounted
 * app-wide by the layout (including on /feedback), so an opener always has it.
 *
 * Sits above the Chat pill (bottom 20–60px), the claim-detail Activity FAB
 * (80–128px below xl) and the /chat FAB stack (to ~128px), where there is no
 * mobile FAB.
 */
export function FeedbackButton() {
  const status = useFeedbackBoardStatus();
  const open = useFeedbackPanelOpen();
  const inMobileFab = useMobileFabItem("report", openFeedbackPanel, { enabled: !!status });

  if (!status) return null;

  return (
    <>
      {!inMobileFab && (
        <button
          type="button"
          onClick={openFeedbackPanel}
          aria-label="Bugs & Features"
          className={cn(
            "fixed bottom-[8.5rem] right-4 z-40 xl:bottom-[4.5rem] xl:right-6",
            "flex h-10 w-10 items-center justify-center gap-2 rounded-full sm:w-auto sm:pl-3 sm:pr-3.5",
            "border border-border bg-card text-foreground shadow-lg transition-all hover:bg-accent hover:shadow-xl active:scale-[0.97]",
            "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 select-none",
          )}
          data-testid="feedback-board-button"
        >
          <Bug className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
          <span className="hidden text-sm font-medium leading-none sm:inline">Bugs & Features</span>
        </button>
      )}
      <FeedbackSubmitDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) closeFeedbackPanel();
        }}
        canSubmit={status.canSubmit}
      />
    </>
  );
}
